import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Link2, FileX2, CheckCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { governanceAPI } from '../../../lib/governanceApi';

const ACTION_LABEL = {
  set_payment_id: 'Stamp real payment id',
  unvoid_and_set_payment_id: 'RESTORE wrongly-voided invoice + stamp payment id',
};

export function InvoiceReconcilePanel({ tenantId, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setBusy(true);
    try {
      const res = await governanceAPI.reconcileInvoices(tenantId, true, false);
      setPlan(res.data);
    } catch (e) {
      toast.error('Could not match invoices', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const apply = async (voidPhantoms) => {
    setBusy(true);
    try {
      const res = await governanceAPI.reconcileInvoices(tenantId, false, voidPhantoms);
      const d = res.data;
      toast.success('Invoices reconciled', {
        description: `${d.applied?.length || 0} fixed · ${d.voided_invoices?.length || 0} voided · ${d.phantom_suspects?.length || 0} phantom flagged`,
      });
      setPlan(null);
      onApplied?.();
    } catch (e) {
      toast.error('Reconcile failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!plan) {
    return (
      <Button size="sm" variant="outline" onClick={preview} disabled={busy} data-testid="invoice-reconcile-preview-btn">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />}
        Match invoices to charges
      </Button>
    );
  }

  const actions = plan.actions || [];
  const phantoms = plan.phantom_suspects || [];
  const nothing = actions.length === 0 && phantoms.length === 0;
  return (
    <div className="mt-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs space-y-2" data-testid="invoice-reconcile-panel">
      <div className="gov-text-secondary">
        Razorpay reports <b>{plan.razorpay_paid_charges}</b> real charge(s). Dry run — nothing changed yet.
      </div>
      {nothing && (
        <div className="flex items-center gap-2 gov-text-tertiary" data-testid="invoice-reconcile-clean">
          <CheckCircle className="h-4 w-4 text-emerald-500" /> Every invoice already matches a real charge.
        </div>
      )}
      {actions.map((a) => (
        <div key={a.invoice_number} className="flex items-start justify-between gap-2 border-b border-[var(--gov-border)] pb-1.5 last:border-0" data-testid={`reconcile-action-${a.invoice_number}`}>
          <div>
            <div className="gov-text-primary">{a.invoice_number} <span className="gov-text-tertiary">· {String(a.invoice_date).slice(0, 10)} · {a.invoice_status}</span></div>
            <div className={a.action === 'unvoid_and_set_payment_id' ? 'text-emerald-500' : 'text-sky-400'}>{ACTION_LABEL[a.action]}</div>
          </div>
          <code className="gov-text-tertiary whitespace-nowrap">{a.old_payment_id} → {a.new_payment_id} ({a.gap_hours}h)</code>
        </div>
      ))}
      {phantoms.map((p) => (
        <div key={p.invoice_number} className="flex items-center justify-between gap-2 text-amber-500" data-testid={`reconcile-phantom-${p.invoice_number}`}>
          <span><FileX2 className="mr-1 inline h-3.5 w-3.5" />{p.invoice_number} · {String(p.invoice_date).slice(0, 10)} — PAID but no Razorpay charge (phantom)</span>
          <span>₹{p.total_amount}</span>
        </div>
      ))}
      {(plan.unmatched_charges || []).map((c) => (
        <div key={c.payment_id} className="text-rose-400" data-testid={`reconcile-unmatched-${c.payment_id}`}>
          Charge {c.payment_id} ({String(c.paid_at).slice(0, 10)}) has NO local invoice — a GST invoice is missing.
        </div>
      ))}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={() => setPlan(null)} disabled={busy} data-testid="invoice-reconcile-cancel-btn">Cancel</Button>
        {actions.length > 0 && (
          <Button size="sm" onClick={() => apply(false)} disabled={busy} data-testid="invoice-reconcile-apply-btn">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Apply {actions.length} fix(es){phantoms.length ? ' (flag phantoms only)' : ''}
          </Button>
        )}
        {phantoms.length > 0 && (
          <Button size="sm" variant="destructive" onClick={() => apply(true)} disabled={busy} data-testid="invoice-reconcile-apply-void-btn">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Apply + void {phantoms.length} phantom(s)
          </Button>
        )}
      </div>
    </div>
  );
}
